<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="breadcrumbItems" />

    <!-- Top Alert Banner mimicking the spreadsheet note -->
    <div class="row mb-3">
      <div class="col-12">
        <div class="alert alert-warning alert-border-left alert-dismissible fade show shadow-sm d-flex flex-wrap align-items-center justify-content-between py-2 px-3 mb-0" role="alert">
          <div class="d-flex align-items-center me-3 py-1">
            <div class="avatar-xs flex-shrink-0 me-3">
              <span class="avatar-title bg-warning text-white rounded-circle fs-16 shadow-sm">
                <i class="ri-alarm-warning-fill"></i>
              </span>
            </div>
            <div>
              <strong class="text-uppercase tracking-wider text-body">{{ $t('t-recruitment-banner-title') }}</strong>
              <span class="ms-1 text-danger fw-semibold">
                {{ $t('t-recruitment-banner-desc') }}
              </span>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 my-1">
            <button class="btn btn-sm btn-outline-dark rounded-pill px-3 shadow-sm py-1" @click="openGlobalAuditModal" :title="$t('t-recruitment-audit-button')">
              <i class="ri-file-history-line align-bottom me-1"></i> {{ $t('t-recruitment-audit-button') }}
            </button>
            <button class="btn btn-sm btn-primary rounded-pill px-3 shadow-sm py-1" @click="openRankingModal">
              <i class="ri-bar-chart-box-line align-bottom me-1"></i> {{ $t('t-recruitment-stats-button') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary KPI Cards -->
    <div class="row g-3 mb-3">
      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">APPLICATIONS</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-secondary text-secondary rounded-circle fs-16">
                  <i class="ri-file-list-3-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0">{{ kpis.totalApplications }}</h4>
              <span class="badge bg-soft-secondary text-secondary px-2 py-1 fs-11">{{ $t('t-recruitment-kpi-total') }}</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ kpis.totalApplications }} {{ $t('t-recruitment-kpi-total').toLowerCase() }}</span>
                <span class="fw-semibold flex-shrink-0">100%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-secondary"
                  role="progressbar"
                  style="width: 100%"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">{{ $t('t-recruitment-kpi-scheduled') }}</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-primary text-primary rounded-circle fs-16">
                  <i class="ri-calendar-event-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0">{{ kpis.totalScheduled }}</h4>
              <span class="badge bg-soft-primary text-primary px-2 py-1 fs-11">Scheduled</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ kpis.totalScheduled }} scheduled</span>
                <span class="fw-semibold flex-shrink-0">{{ kpis.totalApplications > 0 ? ((kpis.totalScheduled / kpis.totalApplications) * 100).toFixed(1) : 0 }}%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-primary"
                  role="progressbar"
                  :style="{ width: (kpis.totalApplications > 0 ? (kpis.totalScheduled / kpis.totalApplications) * 100 : 0) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">{{ $t('t-recruitment-kpi-attendance') }}</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-info text-info rounded-circle fs-16">
                  <i class="ri-user-follow-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0 text-info">{{ kpis.showRate }}%</h4>
              <span class="badge bg-soft-info text-info px-2 py-1 fs-11">{{ $t('t-recruitment-ranking-th-show-rate') }}</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ kpis.totalAttended }} {{ $t('t-recruitment-kpi-attended') }}</span>
                <span class="fw-semibold flex-shrink-0">{{ kpis.showRate }}%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-info"
                  role="progressbar"
                  :style="{ width: Math.min(100, kpis.showRate) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">{{ $t('t-recruitment-kpi-paid-license') }}</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-success text-success rounded-circle fs-16">
                  <i class="ri-money-dollar-circle-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0 text-success">{{ kpis.totalPaidLicense }}</h4>
              <span class="badge bg-soft-success text-success px-2 py-1 fs-11">{{ $t('t-recruitment-kpi-key-target') }}</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ $t('t-recruitment-rate-interview-eff') }}</span>
                <span class="fw-semibold flex-shrink-0">{{ licenseRate }}%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-success"
                  role="progressbar"
                  :style="{ width: Math.min(100, licenseRate) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">{{ $t('t-recruitment-kpi-signed-contract') }}</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-primary text-primary rounded-circle fs-16">
                  <i class="ri-file-text-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0 text-primary">{{ kpis.totalSignedContract }}</h4>
              <span class="badge bg-soft-primary text-primary px-2 py-1 fs-11">{{ $t('t-recruitment-kpi-closing') }}</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ $t('t-recruitment-rate-contract-close') }}</span>
                <span class="fw-semibold flex-shrink-0">{{ contractRate }}%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-primary"
                  role="progressbar"
                  :style="{ width: Math.min(100, contractRate) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-xl col-md-4 col-sm-6">
        <div class="card card-animate border-0 shadow-sm h-100">
          <div class="card-body p-3 d-flex flex-column justify-content-between">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <p class="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">{{ $t('t-recruitment-kpi-pending-payment') }}</p>
              <div class="avatar-xs flex-shrink-0 ms-2">
                <span class="avatar-title bg-soft-warning text-warning rounded-circle fs-16">
                  <i class="ri-time-line"></i>
                </span>
              </div>
            </div>
            <div class="d-flex align-items-center justify-content-between mb-2">
              <h4 class="fs-22 fw-bold mb-0 text-warning">{{ kpis.totalFollowUp }}</h4>
              <span class="badge bg-soft-warning text-warning px-2 py-1 fs-11">{{ $t('t-recruitment-kpi-followup') }}</span>
            </div>
            <div>
              <div class="d-flex align-items-center justify-content-between text-muted fs-11 mb-1" style="min-height: 16px;">
                <span class="text-truncate me-2">{{ $t('t-recruitment-kpi-followup') }}</span>
                <span class="fw-semibold flex-shrink-0">{{ followUpRate }}%</span>
              </div>
              <div class="progress progress-sm" style="height: 5px;">
                <div
                  class="progress-bar bg-warning"
                  role="progressbar"
                  :style="{ width: Math.min(100, followUpRate) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Table / Spreadsheet Card -->
    <div class="card border-0 shadow-sm">
      <div class="card-header border-0 py-3">
        <div class="row g-3 align-items-center">
          <div class="col-lg-3 col-md-6">
            <div class="search-box position-relative">
              <input
                type="text"
                class="form-control"
                :placeholder="$t('t-recruitment-search-placeholder')"
                v-model="filters.search"
                @keyup.enter="onFilterChange"
              />
              <i class="ri-search-line search-icon position-absolute" style="top: 10px; left: 12px;"></i>
            </div>
          </div>

          <div class="col-lg-2 col-md-6">
            <select class="form-select" v-model="filters.departmentID" @change="onFilterChange" :disabled="!isSuperAdmin && visibleDepartments.length <= 1">
              <option value="ALL" v-if="isSuperAdmin || visibleDepartments.length > 1">{{ $t('t-recruitment-all-departments') }}</option>
              <option value="" v-if="!isSuperAdmin && visibleDepartments.length === 0" disabled>Sin departamento asignado</option>
              <option v-for="dept in visibleDepartments" :key="dept.departmentID" :value="dept.departmentID">
                {{ dept.name }}
              </option>
            </select>
          </div>

          <div class="col-lg-2 col-md-4">
            <select class="form-select" v-model="filters.datePreset" @change="applyDatePreset">
              <option value="today">{{ $t('t-recruitment-preset-today') }}</option>
              <option value="yesterday">{{ $t('t-recruitment-preset-yesterday') }}</option>
              <option value="this_week">{{ $t('t-recruitment-preset-this-week') }}</option>
              <option value="this_month">{{ $t('t-recruitment-preset-this-month') }}</option>
              <option value="custom">{{ $t('t-recruitment-preset-custom') }}</option>
              <option value="all">{{ $t('t-recruitment-preset-all') }}</option>
            </select>
          </div>

          <div class="col-lg-2 col-md-4" v-if="filters.datePreset === 'custom'">
            <input type="date" class="form-control" v-model="filters.startDate" @change="onFilterChange" />
          </div>
          <div class="col-lg-2 col-md-4" v-if="filters.datePreset === 'custom'">
            <input type="date" class="form-control" v-model="filters.endDate" @change="onFilterChange" />
          </div>

          <div class="col-lg-2 col-md-4">
            <select class="form-select" v-model="filters.status" @change="onFilterChange">
              <option value="">{{ $t('t-recruitment-all-statuses') }}</option>
              <option value="FIRMÓ CONTRATO">{{ $t('t-recruitment-status-contract') }}</option>
              <option value="PAGÓ LICENCIA">{{ $t('t-recruitment-status-paid') }}</option>
              <option value="QUEDÓ POR PAGAR FOLLOW UP">{{ $t('t-recruitment-status-followup') }}</option>
              <option value="NO RESPONDE">{{ $t('t-recruitment-status-no-response') }}</option>
              <option value="NO INTERESADO">{{ $t('t-recruitment-status-not-interested') }}</option>
              <option value="SE SALIÓ DE LA ENTREVISTA">{{ $t('t-recruitment-status-left-interview') }}</option>
              <option value="PENDIENTE">{{ $t('t-recruitment-status-pending') }}</option>
            </select>
          </div>

          <div class="col-lg-1 col-md-2 ms-auto d-flex justify-content-end">
            <button class="btn btn-outline-secondary w-100" @click="fetchReport" :disabled="loading" :title="$t('t-recruitment-refresh')">
              <i class="ri-refresh-line" :class="{ 'ri-spin': loading }"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Table Section -->
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-nowrap align-middle mb-0 recruitment-grid-table">
            <thead class="table-dark text-uppercase fs-11">
              <tr>
                <th scope="col" style="width: 50px; min-width: 50px;" class="text-center">{{ $t('t-recruitment-th-num') }}</th>
                <th scope="col" style="width: 130px; min-width: 130px;">{{ $t('t-recruitment-th-date') }}</th>
                <th scope="col" style="width: 290px; min-width: 290px;">{{ $t('t-recruitment-th-candidate') }}</th>
                <th scope="col" style="width: 160px; min-width: 160px;">{{ $t('t-recruitment-th-department') }}</th>
                <th scope="col" style="width: 110px; min-width: 110px;" class="text-center">{{ $t('t-recruitment-th-attended') }}</th>
                <th scope="col" style="width: 260px; min-width: 260px;">{{ $t('t-recruitment-th-status') }}</th>
                <th scope="col" style="min-width: 280px;">{{ $t('t-recruitment-th-notes') }}</th>
                <th scope="col" style="width: 95px; min-width: 95px;" class="text-center">{{ $t('t-recruitment-th-license') }}</th>
                <th scope="col" style="width: 170px; min-width: 170px;" class="text-center">{{ $t('t-recruitment-th-last-modified') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="9" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2 text-muted mb-0">{{ $t('t-recruitment-loading') }}</p>
                </td>
              </tr>

              <tr v-else-if="records.length === 0">
                <td colspan="9" class="text-center py-5 text-muted">
                  <i class="ri-user-search-line fs-36 text-secondary d-block mb-2"></i>
                  {{ $t('t-recruitment-no-records') }}
                </td>
              </tr>

              <tr v-else v-for="(item, idx) in records" :key="item.userID" :class="{ 'table-active': item.saving }">
                <!-- Row Number -->
                <td class="text-center text-muted fw-semibold fs-12">
                  {{ idx + 1 }}
                </td>

                <!-- Date (createdOn) -->
                <td>
                  <span class="fw-medium">{{ formatDate(item.createdOn) }}</span>
                  <div class="text-muted fs-11">{{ formatTime(item.createdOn) }}</div>
                </td>

                <!-- Candidate -->
                <td style="min-width: 290px;">
                  <div class="d-flex align-items-center justify-content-between gap-2">
                    <span class="fw-bold fs-13">{{ item.firstName }} {{ item.lastName }}</span>
                    <span
                      v-if="hasValidInterview(item.interviewDate)"
                      class="badge bg-soft-info text-info fs-11 flex-shrink-0"
                      :title="$t('t-recruitment-interview-scheduled') + ' ' + item.interviewDate"
                    >
                      <i class="ri-calendar-event-line me-1"></i>{{ $t('t-recruitment-interview-app') }} {{ formatInterviewDate(item.interviewDate) }}
                    </span>
                  </div>
                  <div class="fs-12 text-muted mt-1" v-if="item.phoneNumber">
                    <span><i class="ri-phone-line me-1"></i>{{ item.phoneNumber }}</span>
                  </div>
                </td>

                <!-- Department -->
                <td style="min-width: 160px;">
                  <span class="badge bg-soft-secondary text-secondary fs-12">{{ item.departmentName }}</span>
                </td>

                <!-- Attended (Sí / No) -->
                <td class="text-center" style="min-width: 110px;">
                  <select
                    class="form-select form-select-sm attended-select"
                    :class="item.attended ? 'attended-yes' : 'attended-no'"
                    v-model="item.attended"
                    @change="updateField(item, 'attended', item.attended)"
                  >
                    <option :value="true">{{ $t('t-recruitment-yes') }}</option>
                    <option :value="false">{{ $t('t-recruitment-no') }}</option>
                  </select>
                </td>

                <!-- Funnel Status Dropdown with Exact Colors -->
                <td style="min-width: 260px;">
                  <div class="position-relative">
                    <select
                      class="form-select form-select-sm fw-bold funnel-status-dropdown"
                      :class="getStatusClass(item.funnelStatus)"
                      v-model="item.funnelStatus"
                      @change="updateField(item, 'funnelStatus', item.funnelStatus)"
                    >
                      <option value="" disabled>{{ $t('t-recruitment-select-status') }}</option>
                      <option value="FIRMÓ CONTRATO">{{ $t('t-recruitment-status-contract') }}</option>
                      <option value="PAGÓ LICENCIA">{{ $t('t-recruitment-status-paid') }}</option>
                      <option value="QUEDÓ POR PAGAR FOLLOW UP">{{ $t('t-recruitment-status-followup') }}</option>
                      <option value="NO RESPONDE">{{ $t('t-recruitment-status-no-response') }}</option>
                      <option value="NO INTERESADO">{{ $t('t-recruitment-status-not-interested') }}</option>
                      <option value="SE SALIÓ DE LA ENTREVISTA">{{ $t('t-recruitment-status-left-interview') }}</option>
                    </select>
                  </div>
                </td>

                <!-- Notes / Observaciones (Integrated with users_usernotes) -->
                <td>
                  <div class="d-flex align-items-center justify-content-between gap-2">
                    <span
                      v-if="item.latestNote"
                      class="text-truncate flex-grow-1 fs-12 text-body cursor-pointer note-snippet"
                      style="max-width: 260px;"
                      :title="$t('t-recruitment-latest-note') + ' ' + item.latestNote"
                      @click="openNotesModal(item)"
                    >
                      <i class="ri-chat-1-fill text-primary me-1"></i>{{ item.latestNote }}
                    </span>
                    <span
                      v-else
                      class="text-muted fs-12 fst-italic cursor-pointer note-snippet"
                      @click="openNotesModal(item)"
                    >
                      {{ $t('t-recruitment-no-notes') }}
                    </span>

                    <button
                      type="button"
                      class="btn btn-sm py-1 px-2 flex-shrink-0"
                      :class="item.notesCount > 0 ? 'btn-soft-primary' : 'btn-outline-secondary'"
                      @click="openNotesModal(item)"
                      :title="$t('t-recruitment-view-add-notes')"
                    >
                      <i class="ri-sticky-note-line align-bottom me-1"></i>
                      <span v-if="item.notesCount > 0">{{ item.notesCount }} {{ item.notesCount === 1 ? $t('t-recruitment-note-singular') : $t('t-recruitment-note-plural') }}</span>
                      <span v-else>{{ $t('t-recruitment-add-note') }}</span>
                    </button>
                  </div>
                </td>

                <!-- License Sequential Order Counter -->
                <td class="text-center">
                  <span
                    v-if="item.funnelStatus === 'PAGÓ LICENCIA' && item.licenseNumberOrder"
                    class="badge bg-success rounded-pill fs-12 px-2 py-1 shadow-sm"
                  >
                    # {{ item.licenseNumberOrder }}
                  </span>
                  <span v-else class="text-muted fs-12">-</span>
                </td>

                <!-- Last Update in Recruitment Report -->
                <td class="text-center">
                  <div
                    v-if="item.updatedOn"
                    class="cursor-pointer audit-trigger-cell py-1 px-2 rounded"
                    @click="openCandidateAuditModal(item)"
                    :title="$t('t-recruitment-view-candidate-audit')"
                  >
                    <span class="fs-12 fw-medium text-body d-block hover-text-primary">
                      {{ formatDate(item.updatedOn) }} {{ formatTime(item.updatedOn) }}
                      <i class="ri-history-line fs-11 text-muted ms-1"></i>
                    </span>
                    <small
                      v-if="item.updatedBy"
                      class="text-muted fs-11 text-truncate d-inline-block"
                      style="max-width: 140px;"
                      :title="$t('t-recruitment-modified-by') + ' ' + item.updatedBy"
                    >
                      <i class="ri-user-follow-line me-1 text-primary"></i>{{ item.updatedBy }}
                    </small>
                  </div>
                  <span v-else class="text-muted fs-12 fst-italic">{{ $t('t-recruitment-no-changes') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Table Footer with Pagination -->
      <div class="card-footer border-top py-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div class="d-flex align-items-center flex-wrap gap-3">
          <span class="text-muted fs-13">
            {{ $t('t-recruitment-pagination-showing') }} <strong>{{ paginationStart }} - {{ paginationEnd }}</strong> {{ $t('t-recruitment-pagination-of') }} <strong>{{ totalRecords }}</strong> {{ $t('t-recruitment-pagination-records') }}
          </span>
          <div class="d-flex align-items-center gap-1">
            <span class="text-muted fs-12">{{ $t('t-recruitment-per-page') }}</span>
            <select class="form-select form-select-sm" style="width: 78px;" v-model="pageSize" @change="changePageSize">
              <option :value="10">10</option>
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
            </select>
          </div>
          <span class="badge bg-soft-success text-success fs-11">
            <i class="ri-check-line me-1"></i>{{ $t('t-recruitment-auto-save') }}
          </span>
        </div>

        <div class="pagination-wrap hstack gap-2" v-if="totalPages > 1">
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page <= 1"
            @click.prevent="changePage(1)"
            :title="$t('t-recruitment-first-page')"
          >
            <i class="ri-skip-back-line"></i>
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page <= 1"
            @click.prevent="changePage(page - 1)"
          >
            <i class="ri-arrow-left-s-line me-1"></i> {{ $t('t-recruitment-previous') }}
          </button>

          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="p in visiblePages"
              :key="p"
              class="page-item"
              :class="{ active: p === page }"
            >
              <a class="page-link" href="#" @click.prevent="changePage(p)">{{ p }}</a>
            </li>
          </ul>

          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page >= totalPages"
            @click.prevent="changePage(page + 1)"
          >
            {{ $t('t-recruitment-next') }} <i class="ri-arrow-right-s-line ms-1"></i>
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            :disabled="page >= totalPages"
            @click.prevent="changePage(totalPages)"
            :title="$t('t-recruitment-last-page')"
          >
            <i class="ri-skip-forward-line"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Ranking & Analytics Modal (5 Statistical Dimensions) -->
    <div
      class="modal fade"
      id="rankingModal"
      tabindex="-1"
      aria-labelledby="rankingModalLabel"
      aria-hidden="true"
      ref="rankingModalRef"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-primary text-white py-3 px-4">
            <div class="d-flex align-items-center">
              <i class="ri-bar-chart-grouped-fill text-warning fs-22 me-2"></i>
              <div>
                <h5 class="modal-title text-white mb-0" id="rankingModalLabel">
                  {{ $t('t-recruitment-stats-button') }}
                </h5>
                <small class="text-white-50 fs-12">{{ filters.startDate }} ~ {{ filters.endDate || $t('t-recruitment-preset-today') }}</small>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body p-4">
            <!-- Navigation Pills / Tabs -->
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4 border-bottom pb-3">
              <ul class="nav nav-pills nav-custom nav-custom-light gap-2" role="tablist">
                <li class="nav-item">
                  <button
                    type="button"
                    class="nav-link py-2 px-3 fw-semibold"
                    :class="{ active: activeAnalyticsTab === 'dept' }"
                    @click="activeAnalyticsTab = 'dept'"
                  >
                    <i class="ri-building-line me-1"></i> {{ $t('t-recruitment-tab-dept-ranking') }}
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    type="button"
                    class="nav-link py-2 px-3 fw-semibold"
                    :class="{ active: activeAnalyticsTab === 'recruiter' }"
                    @click="activeAnalyticsTab = 'recruiter'"
                  >
                    <i class="ri-user-star-line me-1"></i> {{ $t('t-recruitment-tab-recruiter-ranking') }}
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    type="button"
                    class="nav-link py-2 px-3 fw-semibold"
                    :class="{ active: activeAnalyticsTab === 'funnel' }"
                    @click="activeAnalyticsTab = 'funnel'"
                  >
                    <i class="ri-filter-3-line me-1"></i> {{ $t('t-recruitment-tab-funnel-dropoff') }}
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    type="button"
                    class="nav-link py-2 px-3 fw-semibold"
                    :class="{ active: activeAnalyticsTab === 'velocity' }"
                    @click="activeAnalyticsTab = 'velocity'"
                  >
                    <i class="ri-speed-up-line me-1"></i> {{ $t('t-recruitment-tab-velocity') }}
                  </button>
                </li>
                <li class="nav-item">
                  <button
                    type="button"
                    class="nav-link py-2 px-3 fw-semibold"
                    :class="{ active: activeAnalyticsTab === 'timing' }"
                    @click="activeAnalyticsTab = 'timing'"
                  >
                    <i class="ri-calendar-check-line me-1"></i> {{ $t('t-recruitment-tab-timing') }}
                  </button>
                </li>
              </ul>

              <button
                class="btn btn-sm btn-outline-secondary"
                @click="refreshModalAnalytics"
                :disabled="loadingRanking || loadingAnalytics"
                :title="$t('t-recruitment-ranking-refresh')"
              >
                <i class="ri-refresh-line" :class="{ 'ri-spin': loadingRanking || loadingAnalytics }"></i> {{ $t('t-recruitment-ranking-refresh') }}
              </button>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loadingRanking || loadingAnalytics" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted mb-0">{{ $t('t-recruitment-ranking-loading') }}</p>
            </div>

            <!-- TAB 1: Department Ranking -->
            <div v-else-if="activeAnalyticsTab === 'dept'">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <p class="text-muted mb-0 fs-13">
                  {{ $t('t-recruitment-ranking-modal-sub') }}
                </p>
              </div>

              <div v-if="rankingList.length === 0" class="text-center py-5 text-muted">
                <i class="ri-bar-chart-2-line fs-36 text-secondary d-block mb-2"></i>
                {{ $t('t-recruitment-ranking-empty') }}
              </div>

              <div v-else>
                <!-- Bar Chart Comparison -->
                <div class="card border shadow-none mb-4">
                  <div class="card-header bg-light py-2 px-3 border-bottom d-flex align-items-center justify-content-between">
                    <h6 class="card-title mb-0 fs-13 fw-bold text-body">
                      <i class="ri-bar-chart-grouped-line text-primary me-1"></i> {{ $t('t-recruitment-ranking-chart-title') }}
                    </h6>
                    <span class="badge bg-soft-primary text-primary fs-11">{{ $t('t-recruitment-ranking-weekly-comp') }}</span>
                  </div>
                  <div class="card-body p-3">
                    <apexchart
                      class="apex-charts"
                      height="280"
                      type="bar"
                      :series="rankingChartSeries"
                      :options="rankingChartOptions"
                    ></apexchart>
                  </div>
                </div>

                <!-- Leaderboard Table -->
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0 text-nowrap">
                    <thead class="table-dark text-uppercase fs-11">
                      <tr>
                        <th class="text-center text-nowrap" style="width: 70px;">{{ $t('t-recruitment-ranking-th-rank') }}</th>
                        <th class="text-nowrap">{{ $t('t-recruitment-ranking-th-dept') }}</th>
                        <th class="text-center text-nowrap">APPLICATIONS</th>
                        <th class="text-center text-nowrap">{{ $t('t-recruitment-ranking-th-scheduled') }}</th>
                        <th class="text-center text-nowrap">{{ $t('t-recruitment-ranking-th-attended') }}</th>
                        <th class="text-center text-nowrap">{{ $t('t-recruitment-ranking-th-show-rate') }}</th>
                        <th class="text-center text-nowrap text-success fw-bold">{{ $t('t-recruitment-ranking-th-paid') }}</th>
                        <th class="text-center text-nowrap text-primary fw-bold">{{ $t('t-recruitment-ranking-th-contract') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in rankingList"
                        :key="item.departmentID"
                        :class="{ 'ranking-gold-row': item.rank === 1 }"
                      >
                        <td class="text-center fw-bold fs-16">
                          <span v-if="item.rank === 1">🥇</span>
                          <span v-else-if="item.rank === 2">🥈</span>
                          <span v-else-if="item.rank === 3">🥉</span>
                          <span v-else class="text-muted fs-14">#{{ item.rank }}</span>
                        </td>
                        <td>
                          <span class="fw-bold">{{ item.departmentName }}</span>
                        </td>
                        <td class="text-center">{{ item.totalApplications }}</td>
                        <td class="text-center">{{ item.totalScheduled }}</td>
                        <td class="text-center">{{ item.totalAttended }}</td>
                        <td class="text-center">
                          <span class="badge bg-soft-info text-info fs-12">{{ item.showRate }}%</span>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-success fs-13 px-2">{{ item.totalPaidLicense }}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge bg-primary fs-13 px-2">{{ item.totalSignedContract }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- TAB 2: Individual Recruiter Leaderboard -->
            <div v-else-if="activeAnalyticsTab === 'recruiter'">
              <div class="mb-3">
                <h6 class="fs-14 fw-bold mb-1">{{ $t('t-recruitment-recruiter-title') }}</h6>
                <p class="text-muted fs-13 mb-0">{{ $t('t-recruitment-recruiter-desc') }}</p>
              </div>

              <div v-if="!analytics.recruiterRanking || analytics.recruiterRanking.length === 0" class="text-center py-5 text-muted">
                <i class="ri-user-star-line fs-36 text-secondary d-block mb-2"></i>
                {{ $t('t-recruitment-recruiter-empty') }}
              </div>

              <div v-else class="table-responsive">
                <table class="table table-hover align-middle mb-0 text-nowrap">
                  <thead class="table-dark text-uppercase fs-11">
                    <tr>
                      <th class="text-center" style="width: 70px;">{{ $t('t-recruitment-ranking-th-rank') }}</th>
                      <th>{{ $t('t-recruitment-recruiter-th-name') }}</th>
                      <th class="text-center text-success fw-bold">{{ $t('t-recruitment-recruiter-th-paid') }}</th>
                      <th class="text-center text-primary fw-bold">{{ $t('t-recruitment-recruiter-th-contract') }}</th>
                      <th class="text-center text-warning fw-bold">{{ $t('t-recruitment-recruiter-th-followups') }}</th>
                      <th class="text-center">{{ $t('t-recruitment-recruiter-th-actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="rec in analytics.recruiterRanking"
                      :key="rec.changedByUserID || rec.recruiterName"
                      :class="{ 'ranking-gold-row': rec.rank === 1 }"
                    >
                      <td class="text-center fw-bold fs-16">
                        <span v-if="rec.rank === 1">🥇</span>
                        <span v-else-if="rec.rank === 2">🥈</span>
                        <span v-else-if="rec.rank === 3">🥉</span>
                        <span v-else class="text-muted fs-14">#{{ rec.rank }}</span>
                      </td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs me-2">
                            <span class="avatar-title bg-soft-primary text-primary rounded-circle fw-bold fs-12">
                              {{ (rec.recruiterName || 'U').substring(0, 2).toUpperCase() }}
                            </span>
                          </div>
                          <span class="fw-bold">{{ rec.recruiterName }}</span>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-success fs-13 px-2">{{ rec.paidLicenses }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-primary fs-13 px-2">{{ rec.signedContracts }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-warning fs-13 px-2">{{ rec.followUps }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-soft-info text-info fs-12 px-2">{{ rec.totalActions }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- TAB 3: Conversion Funnel & Drop-off Breakdown -->
            <div v-else-if="activeAnalyticsTab === 'funnel'">
              <div class="mb-3">
                <h6 class="fs-14 fw-bold mb-1">{{ $t('t-recruitment-funnel-title') }}</h6>
                <p class="text-muted fs-13 mb-0">{{ $t('t-recruitment-funnel-desc') }}</p>
              </div>

              <!-- Step-by-Step Funnel Cards -->
              <div class="row g-3 mb-4 text-center align-items-center">
                <!-- Step 1: Convocados -->
                <div class="col-lg-3 col-6">
                  <div class="card border border-primary border-opacity-25 shadow-none mb-0 bg-soft-primary h-100">
                    <div class="card-body p-3">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title bg-primary text-white rounded-circle fs-14">1</span>
                      </div>
                      <p class="text-muted fs-12 mb-1 text-uppercase fw-semibold">{{ $t('t-recruitment-kpi-scheduled') }}</p>
                      <h4 class="fw-bold mb-1 text-primary">{{ analytics.funnel.scheduled }}</h4>
                      <span class="badge bg-primary text-white fs-11">100% Base</span>
                    </div>
                  </div>
                </div>

                <!-- Step 2: Asistieron -->
                <div class="col-lg-3 col-6">
                  <div class="card border border-info border-opacity-25 shadow-none mb-0 bg-soft-info h-100">
                    <div class="card-body p-3">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title bg-info text-white rounded-circle fs-14">2</span>
                      </div>
                      <p class="text-muted fs-12 mb-1 text-uppercase fw-semibold">{{ $t('t-recruitment-kpi-attendance') }}</p>
                      <h4 class="fw-bold mb-1 text-info">{{ analytics.funnel.attended }}</h4>
                      <span class="badge bg-info text-white fs-11">{{ analytics.funnel.showRate }}% Show Rate</span>
                    </div>
                  </div>
                </div>

                <!-- Step 3: Pagó Licencia -->
                <div class="col-lg-3 col-6">
                  <div class="card border border-success border-opacity-25 shadow-none mb-0 bg-soft-success h-100">
                    <div class="card-body p-3">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title bg-success text-white rounded-circle fs-14">3</span>
                      </div>
                      <p class="text-muted fs-12 mb-1 text-uppercase fw-semibold">{{ $t('t-recruitment-kpi-paid-license') }}</p>
                      <h4 class="fw-bold mb-1 text-success">{{ analytics.funnel.paid }}</h4>
                      <span class="badge bg-success text-white fs-11">{{ analytics.funnel.showToPaidRate }}% Show ➔ Paid</span>
                    </div>
                  </div>
                </div>

                <!-- Step 4: Firmó Contrato -->
                <div class="col-lg-3 col-6">
                  <div class="card border border-dark border-opacity-25 shadow-none mb-0 bg-soft-dark h-100">
                    <div class="card-body p-3">
                      <div class="avatar-xs mx-auto mb-2">
                        <span class="avatar-title bg-dark text-white rounded-circle fs-14">4</span>
                      </div>
                      <p class="text-muted fs-12 mb-1 text-uppercase fw-semibold">{{ $t('t-recruitment-kpi-signed-contract') }}</p>
                      <h4 class="fw-bold mb-1 text-body">{{ analytics.funnel.contract }}</h4>
                      <span class="badge bg-dark text-white fs-11">{{ analytics.funnel.paidToContractRate }}% Paid ➔ Contract</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Overall Conversion Banner -->
              <div class="alert alert-success d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 border-0">
                <div class="d-flex align-items-center">
                  <i class="ri-checkbox-circle-fill fs-24 me-3 text-success"></i>
                  <div>
                    <h6 class="alert-heading mb-0 fw-bold">{{ $t('t-recruitment-rate-overall') }}</h6>
                    <small class="text-muted">{{ analytics.funnel.contract }} {{ $t('t-recruitment-dropoff-banner-sub') }} {{ analytics.funnel.scheduled }} {{ $t('t-recruitment-dropoff-banner-sub-end') }}</small>
                  </div>
                </div>
                <h3 class="fw-bold text-success mb-0">{{ analytics.funnel.overallConversionRate }}%</h3>
              </div>

              <!-- Drop-off Breakdown Row -->
              <div class="card border shadow-sm mb-0">
                <div class="card-header bg-light py-2 px-3 border-bottom d-flex align-items-center justify-content-between">
                  <h6 class="card-title mb-0 fs-13 fw-bold text-body">
                    <i class="ri-pie-chart-2-line text-primary me-1"></i> {{ $t('t-recruitment-dropoff-title') }}
                  </h6>
                  <span class="badge bg-soft-secondary text-secondary fs-11">{{ $t('t-recruitment-dropoff-desc') }}</span>
                </div>
                <div class="card-body p-3">
                  <div class="row align-items-center g-4">
                    <div class="col-lg-5 text-center d-flex flex-column align-items-center justify-content-center">
                      <apexchart
                        v-if="dropoffChartSeries.length > 0 && dropoffChartSeries.some(v => v > 0)"
                        class="apex-charts w-100"
                        height="320"
                        type="donut"
                        :series="dropoffChartSeries"
                        :options="dropoffChartOptions"
                      ></apexchart>
                      <div v-else class="text-center py-5 text-muted">
                        <i class="ri-check-double-line fs-36 text-success d-block mb-2"></i>
                        <p class="mb-0 fs-13">{{ $t('t-recruitment-dropoff-empty') }}</p>
                      </div>
                    </div>
                    <div class="col-lg-7">
                      <div class="table-responsive">
                        <table class="table table-hover align-middle mb-0 dropoff-table">
                          <thead class="table-light text-uppercase fs-11">
                            <tr>
                              <th>{{ $t('t-recruitment-dropoff-th-cause') }}</th>
                              <th class="text-center" style="width: 110px;">{{ $t('t-recruitment-dropoff-th-count') }}</th>
                              <th class="text-end" style="width: 170px;">{{ $t('t-recruitment-dropoff-th-pct') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="item in analytics.dropoff" :key="item.key" :class="{ 'opacity-50': item.count === 0 }">
                              <td>
                                <div class="d-flex align-items-center">
                                  <span
                                    class="d-inline-block rounded-circle me-2 flex-shrink-0 shadow-sm"
                                    :style="{ width: '12px', height: '12px', backgroundColor: item.color }"
                                  ></span>
                                  <span class="fs-12 fw-semibold">{{ getDropoffLabel(item.key) }}</span>
                                </div>
                              </td>
                              <td class="text-center">
                                <span
                                  class="badge rounded-pill px-2 py-1 fs-12 fw-bold"
                                  :class="item.count > 0 ? 'bg-soft-dark text-body border' : 'bg-light text-muted'"
                                >
                                  {{ item.count }}
                                </span>
                              </td>
                              <td class="text-end">
                                <div class="d-flex align-items-center justify-content-end gap-2">
                                  <div class="progress progress-sm flex-grow-1" style="height: 6px; min-width: 60px;">
                                    <div
                                      class="progress-bar rounded-pill"
                                      role="progressbar"
                                      :style="{ width: item.percentage + '%', backgroundColor: item.color }"
                                    ></div>
                                  </div>
                                  <span
                                    class="badge fs-11 px-2 fw-bold"
                                    :style="{ backgroundColor: item.color + '22', color: item.color, border: '1px solid ' + item.color + '44' }"
                                  >
                                    {{ item.percentage }}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB 4: Conversion Velocity -->
            <div v-else-if="activeAnalyticsTab === 'velocity'">
              <div class="mb-3">
                <h6 class="fs-14 fw-bold mb-1">{{ $t('t-recruitment-velocity-title') }}</h6>
                <p class="text-muted fs-13 mb-0">{{ $t('t-recruitment-velocity-desc') }}</p>
              </div>

              <!-- Velocity KPI Cards -->
              <div class="row g-3 mb-4">
                <div class="col-md-6 col-lg-3">
                  <div class="card border border-success border-opacity-25 shadow-none mb-0 bg-soft-success h-100">
                    <div class="card-body p-3">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="fs-12 fw-semibold text-muted text-uppercase">{{ $t('t-recruitment-avg-days-paid') }}</span>
                        <i class="ri-time-line text-success fs-18"></i>
                      </div>
                      <h3 class="fw-bold text-success mb-1">{{ analytics.velocity.avgDaysToPaid }}</h3>
                      <small class="text-muted">{{ $t('t-recruitment-days') }} desde registro a pago de licencia</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 col-lg-3">
                  <div class="card border border-primary border-opacity-25 shadow-none mb-0 bg-soft-primary h-100">
                    <div class="card-body p-3">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="fs-12 fw-semibold text-muted text-uppercase">{{ $t('t-recruitment-avg-days-contract') }}</span>
                        <i class="ri-file-text-line text-primary fs-18"></i>
                      </div>
                      <h3 class="fw-bold text-primary mb-1">{{ analytics.velocity.avgDaysToContract }}</h3>
                      <small class="text-muted">{{ $t('t-recruitment-days') }} desde registro a firma de contrato</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 col-lg-3">
                  <div class="card border border-warning border-opacity-25 shadow-none mb-0 bg-soft-warning h-100">
                    <div class="card-body p-3">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="fs-12 fw-semibold text-muted text-uppercase">{{ $t('t-recruitment-fast-conversions') }}</span>
                        <i class="ri-flashlight-line text-warning fs-18"></i>
                      </div>
                      <h3 class="fw-bold text-warning mb-1">{{ analytics.velocity.fastConversions }}</h3>
                      <small class="text-muted">{{ $t('t-recruitment-candidates') }} cerrados en su primera semana</small>
                    </div>
                  </div>
                </div>

                <div class="col-md-6 col-lg-3">
                  <div class="card border border-info border-opacity-25 shadow-none mb-0 bg-soft-info h-100">
                    <div class="card-body p-3">
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <span class="fs-12 fw-semibold text-muted text-uppercase">{{ $t('t-recruitment-recovered-leads') }}</span>
                        <i class="ri-recycle-line text-info fs-18"></i>
                      </div>
                      <h3 class="fw-bold text-info mb-1">{{ analytics.velocity.recoveredLeads }}</h3>
                      <small class="text-muted">{{ $t('t-recruitment-candidates') }} reactivados tras más de 30 días</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Explanatory Insight -->
              <div class="card border shadow-none bg-light mb-0">
                <div class="card-body p-3">
                  <div class="d-flex">
                    <i class="ri-lightbulb-line fs-20 text-warning me-2 flex-shrink-0"></i>
                    <div>
                      <h6 class="fs-13 fw-bold mb-1">Estrategia de Cierre y Pipeline</h6>
                      <p class="fs-12 text-muted mb-0">
                        Los cierres rápidos (≤ 7 días) reflejan la efectividad de la reunión de entrevista grupal. 
                        Por otro lado, los <strong>Leads Recuperados (> 30 días)</strong> demuestran la constancia del equipo de reclutadoras 
                        en dar seguimiento a prospectos que no pagaron inmediatamente pero fueron rescatados semanas después.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB 5: Day & Time Performance Analysis -->
            <div v-else-if="activeAnalyticsTab === 'timing'">
              <div class="mb-3">
                <h6 class="fs-14 fw-bold mb-1">{{ $t('t-recruitment-timing-title') }}</h6>
                <p class="text-muted fs-13 mb-0">{{ $t('t-recruitment-timing-desc') }}</p>
              </div>

              <!-- Highlight recommendations -->
              <div class="row g-3 mb-4">
                <div class="col-md-6">
                  <div class="card border border-success border-opacity-25 bg-soft-success shadow-none mb-0">
                    <div class="card-body p-3 d-flex align-items-center">
                      <div class="avatar-sm me-3">
                        <span class="avatar-title bg-success text-white rounded-circle fs-18">
                          <i class="ri-calendar-check-line"></i>
                        </span>
                      </div>
                      <div>
                        <small class="text-muted text-uppercase fw-semibold fs-11">{{ $t('t-recruitment-best-day') }}</small>
                        <h4 class="fw-bold text-success mb-0">{{ analytics.timing.bestDay || '-' }}</h4>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card border border-info border-opacity-25 bg-soft-info shadow-none mb-0">
                    <div class="card-body p-3 d-flex align-items-center">
                      <div class="avatar-sm me-3">
                        <span class="avatar-title bg-info text-white rounded-circle fs-18">
                          <i class="ri-time-line"></i>
                        </span>
                      </div>
                      <div>
                        <small class="text-muted text-uppercase fw-semibold fs-11">{{ $t('t-recruitment-best-slot') }}</small>
                        <h4 class="fw-bold text-info mb-0">{{ analytics.timing.bestTimeSlot || '-' }}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Day of week chart + table -->
              <div class="row g-3">
                <div class="col-lg-7">
                  <div class="card border shadow-none mb-0 h-100">
                    <div class="card-header bg-light py-2 px-3 border-bottom">
                      <h6 class="card-title mb-0 fs-13 fw-bold text-body">
                        <i class="ri-bar-chart-line text-primary me-1"></i> Asistencia por Día de la Semana
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <apexchart
                        class="apex-charts"
                        height="240"
                        type="bar"
                        :series="timingChartSeries"
                        :options="timingChartOptions"
                      ></apexchart>
                    </div>
                  </div>
                </div>

                <div class="col-lg-5">
                  <div class="card border shadow-none mb-0 h-100">
                    <div class="card-header bg-light py-2 px-3 border-bottom">
                      <h6 class="card-title mb-0 fs-13 fw-bold text-body">
                        <i class="ri-time-line text-info me-1"></i> Rendimiento por Franja Horaria
                      </h6>
                    </div>
                    <div class="card-body p-3">
                      <div class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                          <thead>
                            <tr class="text-muted fs-11 text-uppercase">
                              <th>Turno</th>
                              <th class="text-center">Show Rate</th>
                              <th class="text-center text-success">Pagaron</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="slot in analytics.timing.byTimeSlot" :key="slot.slot">
                              <td>
                                <span class="fs-12 fw-semibold d-block">{{ slot.label }}</span>
                                <small class="text-muted fs-11">{{ slot.scheduled }} citados</small>
                              </td>
                              <td class="text-center">
                                <span class="badge bg-soft-info text-info fs-12">{{ slot.showRate }}%</span>
                              </td>
                              <td class="text-center">
                                <span class="badge bg-success fs-12 px-2">{{ slot.paid }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer bg-light py-2">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">{{ $t('t-recruitment-close') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Notes Modal (Unified userNotes system like /agents/List) -->
    <div
      id="open-candidate-notes"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="notesModalLabel"
      aria-hidden="true"
      ref="notesModalRef"
    >
      <div class="modal-dialog modal-dialog-centered" style="max-width: 580px;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
            <h6 class="modal-title mb-0 fs-14 fw-semibold text-muted">
              <i class="ri-chat-history-line me-1 text-primary"></i> {{ $t('t-recruitment-notes-modal-title') }}
            </h6>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-0">
            <notes
              v-if="notesData && notesData.length > 0"
              :dataList="notesData"
              @onFire="refreshCandidateNotes"
              :idNumber="99"
            ></notes>
            <div v-else class="p-5 text-center">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted mb-0">{{ $t('t-recruitment-notes-loading') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Audit Log Modal (Historial de Auditoría de Cambios) -->
    <div
      id="auditLogModal"
      class="modal fade"
      tabindex="-1"
      aria-labelledby="auditLogModalLabel"
      aria-hidden="true"
      ref="auditModalRef"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-dark text-white py-3 px-4">
            <h5 class="modal-title text-white d-flex align-items-center" id="auditLogModalLabel">
              <i class="ri-file-history-fill text-warning fs-20 me-2"></i>
              <span v-if="selectedAuditCandidate">
                {{ $t('t-recruitment-audit-modal-title-candidate') }} {{ selectedAuditCandidate.firstName }} {{ selectedAuditCandidate.lastName }}
              </span>
              <span v-else>
                {{ $t('t-recruitment-audit-modal-title-global') }}
              </span>
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body p-4">
            <!-- Modal Filters Bar -->
            <div class="row g-3 align-items-center mb-3">
              <div class="col-md-6 col-lg-5">
                <div class="search-box position-relative">
                  <input
                    type="text"
                    class="form-control"
                    :placeholder="$t('t-recruitment-audit-search-placeholder')"
                    v-model="auditFilters.search"
                    @keyup.enter="fetchAuditLogs"
                  />
                  <i class="ri-search-line search-icon position-absolute" style="top: 10px; left: 12px;"></i>
                </div>
              </div>

              <div class="col-md-6 col-lg-7 d-flex justify-content-end align-items-center gap-2 flex-wrap">
                <button
                  v-if="selectedAuditCandidate"
                  class="btn btn-sm btn-outline-primary"
                  @click="openGlobalAuditModal"
                >
                  <i class="ri-global-line me-1"></i> {{ $t('t-recruitment-audit-view-all') }}
                </button>

                <button
                  class="btn btn-sm btn-outline-secondary"
                  @click="fetchAuditLogs"
                  :disabled="loadingAudit"
                  :title="$t('t-recruitment-ranking-refresh')"
                >
                  <i class="ri-refresh-line" :class="{ 'ri-spin': loadingAudit }"></i> {{ $t('t-recruitment-ranking-refresh') }}
                </button>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loadingAudit" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 text-muted mb-0">{{ $t('t-recruitment-audit-loading') }}</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="auditLogs.length === 0" class="text-center py-5 text-muted">
              <i class="ri-history-line fs-36 text-secondary d-block mb-2"></i>
              {{ $t('t-recruitment-audit-empty') }}
            </div>

            <!-- Audit Table -->
            <div v-else class="table-responsive">
              <table class="table table-hover align-middle mb-0 text-nowrap audit-table">
                <thead class="table-light text-uppercase fs-11">
                  <tr>
                    <th scope="col" style="width: 140px;">{{ $t('t-recruitment-audit-th-datetime') }}</th>
                    <th scope="col" v-if="!selectedAuditCandidate" style="width: 200px;">{{ $t('t-recruitment-audit-th-candidate') }}</th>
                    <th scope="col" style="width: 180px;">{{ $t('t-recruitment-audit-th-user') }}</th>
                    <th scope="col" style="width: 160px;">{{ $t('t-recruitment-audit-th-field') }}</th>
                    <th scope="col">{{ $t('t-recruitment-audit-th-detail') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="log in auditLogs" :key="log.id">
                    <!-- Timestamp -->
                    <td>
                      <span class="fw-semibold fs-12">{{ formatDate(log.createdOn) }}</span>
                      <div class="text-muted fs-11">{{ formatTime(log.createdOn) }}</div>
                    </td>

                    <!-- Candidate (if global) -->
                    <td v-if="!selectedAuditCandidate">
                      <span class="fw-bold fs-12">{{ log.candidateName }}</span>
                    </td>

                    <!-- Changed By User -->
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="avatar-xs flex-shrink-0 me-2">
                          <span class="avatar-title bg-soft-primary text-primary rounded-circle fs-13">
                            <i class="ri-user-settings-line"></i>
                          </span>
                        </div>
                        <div>
                          <span class="fw-medium fs-12 d-block">{{ log.changedByName || $t('t-recruitment-audit-system') }}</span>
                          <small class="text-muted fs-11" v-if="log.changedByEmail">{{ log.changedByEmail }}</small>
                        </div>
                      </div>
                    </td>

                    <!-- Field Changed Badge -->
                    <td>
                      <span class="badge bg-soft-secondary text-secondary fs-12">
                        {{ formatFieldLabel(log.fieldChanged) }}
                      </span>
                    </td>

                    <!-- Diff: Old Value -> New Value -->
                    <td>
                      <div class="d-flex align-items-center flex-wrap gap-2">
                        <span class="badge bg-soft-danger text-danger fs-12 py-1 px-2">
                          <i class="ri-close-circle-line me-1"></i>{{ log.oldValue || $t('t-recruitment-audit-empty-val') }}
                        </span>
                        <i class="ri-arrow-right-line text-muted fs-14"></i>
                        <span class="badge bg-soft-success text-success fs-12 py-1 px-2">
                          <i class="ri-checkbox-circle-line me-1"></i>{{ log.newValue || $t('t-recruitment-audit-empty-val') }}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Audit Pagination Footer -->
            <div v-if="auditPagination.totalPages > 1" class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
              <span class="text-muted fs-12">
                {{ $t('t-recruitment-audit-page') }} {{ auditPagination.page }} {{ $t('t-recruitment-audit-of') }} {{ auditPagination.totalPages }} ({{ auditPagination.totalRecords }} {{ $t('t-recruitment-audit-changes-recorded') }})
              </span>
              <div class="hstack gap-2">
                <button
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="auditPagination.page <= 1"
                  @click="changeAuditPage(auditPagination.page - 1)"
                >
                  <i class="ri-arrow-left-s-line me-1"></i> {{ $t('t-recruitment-previous') }}
                </button>
                <button
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="auditPagination.page >= auditPagination.totalPages"
                  @click="changeAuditPage(auditPagination.page + 1)"
                >
                  {{ $t('t-recruitment-next') }} <i class="ri-arrow-right-s-line ms-1"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="modal-footer bg-light py-2">
            <button type="button" class="btn btn-secondary btn-sm" data-bs-dismiss="modal">{{ $t('t-recruitment-close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import axios from "axios";
import { Modal } from "bootstrap";
import jwt_decode from "jwt-decode";
import notes from "@/components/users/notes.vue";

export default {
  name: "RecruitmentReport",
  components: {
    Layout,
    PageHeader,
    notes
  },
  data() {
    return {
      isSuperAdmin: false,
      userDepartmentID: null,
      loading: false,
      loadingRanking: false,
      records: [],
      rankingList: [],
      departments: [],
      page: 1,
      pageSize: 25,
      totalRecords: 0,
      totalPages: 1,
      kpis: {
        totalApplications: 0,
        totalScheduled: 0,
        totalAttended: 0,
        showRate: 0,
        totalPaidLicense: 0,
        totalSignedContract: 0,
        totalFollowUp: 0
      },
      filters: {
        search: "",
        departmentID: "ALL",
        datePreset: "this_month",
        startDate: "",
        endDate: "",
        status: ""
      },
      rankingModalInstance: null,
      activeAnalyticsTab: "dept",
      loadingAnalytics: false,
      analytics: {
        funnel: {
          scheduled: 0,
          attended: 0,
          paid: 0,
          contract: 0,
          showRate: 0,
          showToPaidRate: 0,
          paidToContractRate: 0,
          overallConversionRate: 0
        },
        dropoff: [],
        velocity: {
          avgDaysToPaid: 0,
          avgDaysToContract: 0,
          fastConversions: 0,
          recoveredLeads: 0
        },
        recruiterRanking: [],
        timing: {
          byDayOfWeek: [],
          byTimeSlot: [],
          bestDay: "-",
          bestTimeSlot: "-"
        }
      },
      notesModalInstance: null,
      notesData: [],
      currentCandidateID: null,
      auditModalInstance: null,
      auditLogs: [],
      loadingAudit: false,
      selectedAuditCandidate: null,
      auditPagination: {
        page: 1,
        pageSize: 15,
        totalRecords: 0,
        totalPages: 1
      },
      auditFilters: {
        search: "",
        startDate: "",
        endDate: ""
      }
    };
  },
  computed: {
    pageTitle() {
      return this.$t("t-recruitment-report");
    },
    breadcrumbItems() {
      return [
        { text: this.$t("t-agents"), href: "/" },
        { text: this.$t("t-recruitment-report"), active: true }
      ];
    },
    visibleDepartments() {
      if (!Array.isArray(this.departments)) {
        return [];
      }
      if (this.isSuperAdmin) {
        return this.departments;
      }
      // A recruiter with special access to several departments (specialAccessDepartment): /api/listDepartments
      // already returns exactly those departments, so all of them are selectable. Without this the list was cut
      // down to the recruiter's own department and the filter stayed disabled.
      if (this.departments.length > 1) {
        return this.departments;
      }
      if (!this.userDepartmentID || this.userDepartmentID === 0 || this.userDepartmentID === "0") {
        return this.departments;
      }
      const filtered = this.departments.filter(
        (d) => d && (d.departmentID == this.userDepartmentID)
      );
      return filtered.length > 0 ? filtered : this.departments;
    },
    paginationStart() {
      if (this.totalRecords === 0) return 0;
      return (this.page - 1) * this.pageSize + 1;
    },
    paginationEnd() {
      return Math.min(this.page * this.pageSize, this.totalRecords);
    },
    visiblePages() {
      const pages = [];
      const maxButtons = 5;
      let start = Math.max(1, this.page - Math.floor(maxButtons / 2));
      let end = Math.min(this.totalPages, start + maxButtons - 1);
      if (end - start + 1 < maxButtons) {
        start = Math.max(1, end - maxButtons + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    contractRate() {
      if (!this.kpis.totalAttended || this.kpis.totalAttended === 0) return 0;
      return Number(((this.kpis.totalSignedContract / this.kpis.totalAttended) * 100).toFixed(1));
    },
    licenseRate() {
      if (!this.kpis.totalAttended || this.kpis.totalAttended === 0) return 0;
      return Number(((this.kpis.totalPaidLicense / this.kpis.totalAttended) * 100).toFixed(1));
    },
    followUpRate() {
      if (!this.kpis.totalScheduled || this.kpis.totalScheduled === 0) return 0;
      return Number(((this.kpis.totalFollowUp / this.kpis.totalScheduled) * 100).toFixed(1));
    },
    rankingChartSeries() {
      if (!this.rankingList || this.rankingList.length === 0) return [];
      return [
        {
          name: "APPLICATIONS",
          data: this.rankingList.map((d) => d.totalApplications || 0)
        },
        {
          name: this.$t("t-recruitment-ranking-th-scheduled"),
          data: this.rankingList.map((d) => d.totalScheduled || 0)
        },
        {
          name: this.$t("t-recruitment-ranking-th-attended"),
          data: this.rankingList.map((d) => d.totalAttended || 0)
        },
        {
          name: this.$t("t-recruitment-ranking-th-paid"),
          data: this.rankingList.map((d) => d.totalPaidLicense || 0)
        },
        {
          name: this.$t("t-recruitment-ranking-th-contract"),
          data: this.rankingList.map((d) => d.totalSignedContract || 0)
        }
      ];
    },
    rankingChartOptions() {
      const isDark = document.documentElement.getAttribute("data-layout-mode") === "dark";
      const categories = (this.rankingList || []).map((d) => d.departmentName || "Sin Depto");
      const personLabel = this.$t("t-recruitment-ranking-chart-person");
      const peopleLabel = this.$t("t-recruitment-ranking-chart-people");
      return {
        chart: {
          type: "bar",
          height: 280,
          toolbar: { show: false },
          fontFamily: "inherit"
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "45%",
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"]
        },
        colors: ["#405189", "#299cdb", "#0ab39c", "#f06548"],
        xaxis: {
          categories: categories,
          labels: {
            style: {
              colors: isDark ? "#adb5bd" : "#495057",
              fontSize: "12px",
              fontWeight: 600
            }
          }
        },
        yaxis: {
          title: {
            text: this.$t("t-recruitment-ranking-chart-yaxis"),
            style: {
              color: isDark ? "#adb5bd" : "#495057",
              fontWeight: 500
            }
          },
          labels: {
            style: {
              colors: isDark ? "#adb5bd" : "#495057"
            }
          }
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          labels: {
            colors: isDark ? "#ced4da" : "#495057"
          }
        },
        grid: {
          borderColor: isDark ? "#32383e" : "#f1f1f1"
        },
        tooltip: {
          theme: isDark ? "dark" : "light",
          y: {
            formatter: function (val) {
              return val + (val === 1 ? " " + personLabel : " " + peopleLabel);
            }
          }
        }
      };
    },
    activeDropoffItems() {
      return (this.analytics.dropoff || []).filter((item) => item.count > 0);
    },
    dropoffChartSeries() {
      return this.activeDropoffItems.map((item) => item.count);
    },
    dropoffChartOptions() {
      const isDark = document.documentElement.getAttribute("data-layout-mode") === "dark";
      const labels = this.activeDropoffItems.map((item) => this.getDropoffLabel(item.key));
      const colors = this.activeDropoffItems.map((item) => item.color);
      const candidatesLabel = this.$t("t-recruitment-candidates");
      return {
        chart: {
          type: "donut",
          height: 320,
          fontFamily: "inherit"
        },
        labels: labels,
        colors: colors,
        legend: {
          show: false
        },
        stroke: {
          show: true,
          width: 3,
          colors: [isDark ? "#212529" : "#ffffff"]
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val >= 8 ? val.toFixed(0) + "%" : "";
          },
          dropShadow: {
            enabled: false
          },
          style: {
            fontSize: "12px",
            fontWeight: 700,
            colors: ["#ffffff"]
          }
        },
        plotOptions: {
          pie: {
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isDark ? "#94a3b8" : "#6c757d",
                  offsetY: -4
                },
                value: {
                  show: true,
                  fontSize: "26px",
                  fontWeight: 800,
                  color: isDark ? "#ffffff" : "#212529",
                  offsetY: 6,
                  formatter: (val) => val
                },
                total: {
                  show: true,
                  label: this.$t("t-recruitment-dropoff-chart-total"),
                  color: isDark ? "#adb5bd" : "#6c757d",
                  fontSize: "11px",
                  fontWeight: 700,
                  formatter: (w) => {
                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  }
                }
              }
            }
          }
        },
        tooltip: {
          theme: isDark ? "dark" : "light",
          y: {
            formatter: function (val) {
              return val + " " + candidatesLabel;
            }
          }
        }
      };
    },
    timingChartSeries() {
      if (!this.analytics.timing || !this.analytics.timing.byDayOfWeek) return [];
      return [
        {
          name: this.$t("t-recruitment-rate-show") + " (%)",
          data: this.analytics.timing.byDayOfWeek.map((d) => d.showRate || 0)
        }
      ];
    },
    timingChartOptions() {
      const isDark = document.documentElement.getAttribute("data-layout-mode") === "dark";
      const categories = (this.analytics.timing?.byDayOfWeek || []).map((d) => d.day);
      return {
        chart: {
          type: "bar",
          height: 240,
          toolbar: { show: false },
          fontFamily: "inherit"
        },
        plotOptions: {
          bar: {
            columnWidth: "40%",
            borderRadius: 4,
            dataLabels: { position: "top" }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val + "%";
          },
          offsetY: -18,
          style: {
            fontSize: "11px",
            colors: [isDark ? "#ced4da" : "#343a40"]
          }
        },
        colors: ["#0ab39c"],
        xaxis: {
          categories: categories,
          labels: {
            style: {
              colors: isDark ? "#adb5bd" : "#495057",
              fontSize: "11px"
            }
          }
        },
        yaxis: {
          max: 100,
          labels: {
            formatter: (v) => v + "%",
            style: { colors: isDark ? "#adb5bd" : "#495057" }
          }
        },
        grid: {
          borderColor: isDark ? "#32383e" : "#f1f1f1"
        },
        tooltip: {
          theme: isDark ? "dark" : "light",
          y: { formatter: (val) => val + "%" }
        }
      };
    }
  },
  mounted() {
    this.detectUserPermissions();
    this.applyDatePreset();
    this.fetchDepartments();
  },
  methods: {
    detectUserPermissions() {
      const token = localStorage.getItem("jwt");
      if (token) {
        try {
          const decoded = jwt_decode(token);
          this.isSuperAdmin = decoded.userGroup === "SUPER_ADMIN_GROUP";
          this.userDepartmentID = decoded.departmentID;
          if (!this.isSuperAdmin && this.userDepartmentID && this.userDepartmentID !== 0 && this.userDepartmentID !== "0") {
            this.filters.departmentID = this.userDepartmentID;
          }
        } catch (e) {
          console.error("Error decoding token:", e);
        }
      }
    },
    onFilterChange() {
      this.page = 1;
      this.fetchReport();
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages && newPage !== this.page) {
        this.page = newPage;
        this.fetchReport();
      }
    },
    changePageSize() {
      this.page = 1;
      this.fetchReport();
    },
    applyDatePreset() {
      const now = new Date();
      const formatDateStr = (d) => d.toISOString().split("T")[0];

      if (this.filters.datePreset === "today") {
        const today = formatDateStr(now);
        this.filters.startDate = today;
        this.filters.endDate = today;
      } else if (this.filters.datePreset === "yesterday") {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        const yStr = formatDateStr(yesterday);
        this.filters.startDate = yStr;
        this.filters.endDate = yStr;
      } else if (this.filters.datePreset === "this_week") {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday
        const monday = new Date(now.setDate(diff));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        this.filters.startDate = formatDateStr(monday);
        this.filters.endDate = formatDateStr(sunday);
      } else if (this.filters.datePreset === "this_month") {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        this.filters.startDate = formatDateStr(firstDay);
        this.filters.endDate = formatDateStr(lastDay);
      } else if (this.filters.datePreset === "all") {
        this.filters.startDate = "";
        this.filters.endDate = "";
      }

      this.page = 1;
      if (this.filters.datePreset !== "custom") {
        this.fetchReport();
      }
    },

    async fetchDepartments() {
      try {
        const token = localStorage.getItem("jwt");
        const resp = await axios.post("/api/listDepartments", { token });
        let depts = [];
        if (resp.data && resp.data !== "void") {
          if (Array.isArray(resp.data)) {
            depts = resp.data.filter((d) => d && d.departmentID);
          } else if (typeof resp.data === "object" && resp.data !== null && resp.data.departmentID) {
            depts = [resp.data];
          }
        }
        this.departments = depts;

        if (!this.isSuperAdmin && depts.length > 0) {
          if (!this.filters.departmentID || this.filters.departmentID === "ALL") {
            this.filters.departmentID = depts[0].departmentID;
            this.fetchReport();
          }
          if (!this.userDepartmentID) {
            this.userDepartmentID = depts[0].departmentID;
          }
        }
      } catch (err) {
        console.error("Error loading departments:", err);
        this.departments = [];
      }
    },

    async fetchReport() {
      this.loading = true;
      try {
        const token = localStorage.getItem("jwt");
        const params = {
          token,
          page: this.page,
          pageSize: this.pageSize,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
          departmentID: this.filters.departmentID,
          search: this.filters.search,
          status: this.filters.status
        };
        const resp = await axios.get("/api/recruitment/report", { params });
        this.records = (resp.data.records || []).map((r) => ({
          ...r,
          attended: Boolean(r.attended),
          originalNotes: r.notes || "",
          saving: false,
          savedBadge: false
        }));
        if (resp.data.pagination) {
          this.totalRecords = resp.data.pagination.totalRecords;
          this.totalPages = resp.data.pagination.totalPages;
          this.page = resp.data.pagination.page;
        }
        this.kpis = resp.data.kpis || this.kpis;
        // Also refresh background analytics for KPI badges
        this.fetchAnalytics();
      } catch (err) {
        console.error("Error fetching recruitment report:", err);
      } finally {
        this.loading = false;
      }
    },

    async updateField(item, field, value) {
      item.saving = true;
      try {
        const token = localStorage.getItem("jwt");
        const payload = {
          token,
          userID: item.userID,
          [field]: value
        };
        const resp = await axios.post("/api/recruitment/report/update", payload);
        if (resp.data) {
          if (resp.data.updatedBy) item.updatedBy = resp.data.updatedBy;
          if (resp.data.updatedOn) item.updatedOn = resp.data.updatedOn;
        }

        // Instant visual feedback
        item.savedBadge = true;
        setTimeout(() => {
          item.savedBadge = false;
        }, 2000);

        // If funnelStatus changed to/from 'PAGÓ LICENCIA' or attended, recalculate stats
        if (field === "funnelStatus" || field === "attended") {
          this.recalculateLocalKPIs();
        }
      } catch (err) {
        console.error("Error updating field:", err);
      } finally {
        item.saving = false;
      }
    },

    openNotesModal(item) {
      this.currentCandidateID = item.userID;
      this.notesData = [];
      localStorage.setItem("selectedUser", item.userID);

      if (!this.notesModalInstance && this.$refs.notesModalRef) {
        this.notesModalInstance = new Modal(this.$refs.notesModalRef);
      }
      if (this.notesModalInstance) {
        this.notesModalInstance.show();
      }

      this.fetchCandidateNotes(item);
    },

    async fetchCandidateNotes(item) {
      try {
        const data = {
          token: localStorage.getItem("jwt"),
          userID: localStorage.getItem("id"),
          agentID: item.userID,
          clientID: item.userID
        };
        const response = await axios.post("/api/getUserNotes", data);
        if (response.data && Array.isArray(response.data) && response.data.length >= 2) {
          this.notesData = response.data;
        } else {
          this.notesData = [[], `${item.firstName || ""} ${item.lastName || ""}`.trim() || "Candidato"];
        }
      } catch (error) {
        console.error("Error fetching notes:", error);
        this.notesData = [[], `${item.firstName || ""} ${item.lastName || ""}`.trim() || "Candidato"];
      }
    },

    async refreshCandidateNotes() {
      if (this.currentCandidateID) {
        try {
          const data = {
            token: localStorage.getItem("jwt"),
            userID: localStorage.getItem("id"),
            agentID: this.currentCandidateID,
            clientID: this.currentCandidateID
          };
          const response = await axios.post("/api/getUserNotes", data);
          if (response.data && Array.isArray(response.data)) {
            this.notesData = response.data;
          }
        } catch (e) {
          console.error("Error refreshing notes:", e);
        }
        this.fetchReport();
      }
    },

    recalculateLocalKPIs() {
      const total = this.records.length;
      const scheduled = this.records.filter((r) => this.hasValidInterview(r.interviewDate)).length;
      const attended = this.records.filter((r) => r.attended).length;
      const paid = this.records.filter((r) => r.funnelStatus === "PAGÓ LICENCIA").length;
      const contract = this.records.filter((r) => r.funnelStatus === "FIRMÓ CONTRATO").length;
      const fu = this.records.filter((r) => r.funnelStatus === "QUEDÓ POR PAGAR FOLLOW UP").length;

      this.kpis.totalApplications = total;
      this.kpis.totalScheduled = scheduled;
      this.kpis.totalAttended = attended;
      this.kpis.showRate = scheduled > 0 ? Number(((attended / scheduled) * 100).toFixed(1)) : 0;
      this.kpis.totalPaidLicense = paid;
      this.kpis.totalSignedContract = contract;
      this.kpis.totalFollowUp = fu;

      // Re-assign license counter order locally
      let count = 0;
      for (let i = this.records.length - 1; i >= 0; i--) {
        if (this.records[i].funnelStatus === "PAGÓ LICENCIA") {
          count++;
          this.records[i].licenseNumberOrder = count;
        } else {
          this.records[i].licenseNumberOrder = null;
        }
      }
    },

    async openRankingModal() {
      if (!this.rankingModalInstance && this.$refs.rankingModalRef) {
        this.rankingModalInstance = new Modal(this.$refs.rankingModalRef);
      }
      if (this.rankingModalInstance) {
        this.rankingModalInstance.show();
      }
      await this.refreshModalAnalytics();
    },

    async refreshModalAnalytics() {
      await Promise.all([this.fetchRanking(), this.fetchAnalytics()]);
    },

    async fetchRanking() {
      this.loadingRanking = true;
      try {
        const token = localStorage.getItem("jwt");
        const resp = await axios.get("/api/recruitment/ranking", {
          params: {
            token,
            startDate: this.filters.startDate,
            endDate: this.filters.endDate
          }
        });
        this.rankingList = resp.data.ranking || [];
      } catch (err) {
        console.error("Error fetching ranking:", err);
      } finally {
        this.loadingRanking = false;
      }
    },

    async fetchAnalytics() {
      this.loadingAnalytics = true;
      try {
        const token = localStorage.getItem("jwt");
        const resp = await axios.get("/api/recruitment/analytics", {
          params: {
            token,
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
            departmentID: this.filters.departmentID
          }
        });
        if (resp.data) {
          this.analytics = resp.data;
        }
      } catch (err) {
        console.error("Error fetching analytics:", err);
      } finally {
        this.loadingAnalytics = false;
      }
    },

    getDropoffLabel(key) {
      const map = {
        noShow: this.$t("t-recruitment-dropoff-noshow"),
        noResponse: this.$t("t-recruitment-dropoff-noresp"),
        notInterested: this.$t("t-recruitment-dropoff-notinterested"),
        followUp: this.$t("t-recruitment-dropoff-followup"),
        leftInterview: this.$t("t-recruitment-dropoff-left"),
        pending: this.$t("t-recruitment-dropoff-pending")
      };
      return map[key] || key;
    },

    formatDate(dateStr) {
      if (!dateStr) return "N/D";
      const str = String(dateStr);
      if (str.length < 10) return str;
      const parts = str.substring(0, 10).split("-");
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`; // MM/DD/YYYY matching Google Sheet
      }
      return str.substring(0, 10);
    },

    formatTime(dateStr) {
      if (!dateStr) return "";
      const str = String(dateStr);
      if (str.includes("T") && str.length >= 16) {
        return str.substring(11, 16);
      }
      if (str.includes(" ") && str.length >= 16) {
        return str.substring(11, 16);
      }
      return "";
    },

    hasValidInterview(dateStr) {
      return Boolean(
        dateStr &&
        !["", "N/D", "N/A", "Invalid date", "None", null, undefined].includes(dateStr)
      );
    },

    formatInterviewDate(dateStr) {
      if (!this.hasValidInterview(dateStr)) return "";
      return this.formatDate(dateStr);
    },

    getStatusClass(status) {
      switch (status) {
        case "FIRMÓ CONTRATO":
          return "status-contract";
        case "PAGÓ LICENCIA":
          return "status-paid";
        case "QUEDÓ POR PAGAR FOLLOW UP":
          return "status-followup";
        case "NO RESPONDE":
          return "status-no-response";
        case "NO INTERESADO":
          return "status-not-interested";
        case "SE SALIÓ DE LA ENTREVISTA":
          return "status-left-interview";
        default:
          return "status-pending";
      }
    },

    openCandidateAuditModal(item) {
      this.selectedAuditCandidate = item;
      this.auditFilters.search = "";
      this.auditPagination.page = 1;
      this.showAuditModal();
      this.fetchAuditLogs();
    },

    openGlobalAuditModal() {
      this.selectedAuditCandidate = null;
      this.auditFilters.search = "";
      this.auditPagination.page = 1;
      this.showAuditModal();
      this.fetchAuditLogs();
    },

    showAuditModal() {
      if (!this.auditModalInstance && this.$refs.auditModalRef) {
        this.auditModalInstance = new Modal(this.$refs.auditModalRef);
      }
      if (this.auditModalInstance) {
        this.auditModalInstance.show();
      }
    },

    async fetchAuditLogs() {
      this.loadingAudit = true;
      try {
        const token = localStorage.getItem("jwt");
        const params = {
          token,
          page: this.auditPagination.page,
          pageSize: this.auditPagination.pageSize,
          search: this.auditFilters.search
        };
        if (this.selectedAuditCandidate) {
          params.userID = this.selectedAuditCandidate.userID;
        }
        const resp = await axios.get("/api/recruitment/audit-logs", { params });
        this.auditLogs = resp.data.logs || [];
        if (resp.data.pagination) {
          this.auditPagination.totalRecords = resp.data.pagination.totalRecords;
          this.auditPagination.totalPages = resp.data.pagination.totalPages;
          this.auditPagination.page = resp.data.pagination.page;
        }
      } catch (err) {
        console.error("Error fetching audit logs:", err);
      } finally {
        this.loadingAudit = false;
      }
    },

    changeAuditPage(newPage) {
      if (newPage >= 1 && newPage <= this.auditPagination.totalPages && newPage !== this.auditPagination.page) {
        this.auditPagination.page = newPage;
        this.fetchAuditLogs();
      }
    },

    formatFieldLabel(field) {
      const labels = {
        attended: this.$t("t-recruitment-audit-field-attended"),
        funnelStatus: this.$t("t-recruitment-audit-field-status"),
        notes: this.$t("t-recruitment-audit-field-notes")
      };
      return labels[field] || field;
    }
  }
};
</script>

<style scoped>
.recruitment-grid-table {
  min-width: 1560px;
}

.recruitment-grid-table th {
  padding: 11px 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.recruitment-grid-table td {
  padding: 9px 14px;
  vertical-align: middle;
}

/* Attended Select Box - Light Mode Default */
.attended-select {
  font-weight: 700;
  border-radius: 20px;
  padding: 5px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  font-size: 12px;
}

.attended-yes {
  background-color: #d1e7dd !important;
  color: #0f5132 !important;
  border-color: #badbcc !important;
}

.attended-no {
  background-color: #f8d7da !important;
  color: #842029 !important;
  border-color: #f5c2c7 !important;
}

/* Funnel Status Dropdowns - Light Mode Default (Matching Google Sheet) */
.funnel-status-dropdown {
  border-radius: 16px;
  padding: 6px 14px;
  font-size: 11.5px;
  letter-spacing: 0.3px;
  border: none !important;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-width: 220px;
  width: 100%;
}

.funnel-status-dropdown:focus {
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}

.status-contract {
  background-color: #bbf7d0 !important;
  color: #14532d !important;
}

.status-paid {
  background-color: #0d6838 !important;
  color: #ffffff !important;
  font-weight: 800;
}

.status-followup {
  background-color: #ffd8a8 !important;
  color: #7c2d12 !important;
}

.status-no-response {
  background-color: #fef08a !important;
  color: #713f12 !important;
}

.status-not-interested {
  background-color: #b91c1c !important;
  color: #ffffff !important;
}

.status-left-interview {
  background-color: #334155 !important;
  color: #ffffff !important;
}

.status-pending {
  background-color: #e9ecef !important;
  color: #495057 !important;
  border: 1px solid #ced4da !important;
}

/* Note cell input - Light Mode Default */
.cell-note-input {
  background-color: #f8fafc;
  border: 1px solid #d8e2ea;
  color: #1e293b;
  border-radius: 6px;
  font-size: 12.5px;
  padding: 5px 12px;
  transition: all 0.2s ease;
}

.cell-note-input::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.cell-note-input:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.cell-note-input:focus {
  background-color: #ffffff;
  border-color: #3b82f6;
  color: #0f172a;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.18);
  outline: none;
}

.cursor-pointer {
  cursor: pointer;
}

.note-snippet {
  transition: color 0.15s ease;
}

.note-snippet:hover {
  color: #405189 !important;
  text-decoration: underline;
}

.ranking-gold-row {
  background-color: rgba(245, 158, 11, 0.12) !important;
}

/* Instant save badge indicator */
.save-indicator {
  top: -8px;
  right: 6px;
  font-size: 10px;
  animation: fadeInOut 2s forwards;
  z-index: 5;
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(4px); }
  20% { opacity: 1; transform: translateY(0); }
  80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-4px); }
}

.audit-trigger-cell {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.audit-trigger-cell:hover {
  background-color: rgba(64, 81, 137, 0.08) !important;
}
</style>

<style>
.chat-conversation {
  max-height: 420px;
  overflow-y: auto;
}

[data-layout-mode="dark"] .audit-trigger-cell:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

[data-layout-mode="dark"] .note-snippet:hover {
  color: #60a5fa !important;
}

[data-layout-mode="dark"] .ranking-gold-row {
  background-color: rgba(245, 158, 11, 0.18) !important;
  color: #fef3c7 !important;
}

[data-layout-mode="dark"] .ranking-gold-row td {
  color: #fef3c7 !important;
}
/* =========================================================
   DARK MODE OVERRIDES (Triggers when data-layout-mode="dark")
   ========================================================= */
[data-layout-mode="dark"] .recruitment-grid-table .cell-note-input {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  color: #f1f5f9 !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .cell-note-input::placeholder {
  color: rgba(255, 255, 255, 0.35) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .cell-note-input:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .cell-note-input:focus {
  background-color: rgba(255, 255, 255, 0.12) !important;
  border-color: #3b82f6 !important;
  color: #ffffff !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .attended-yes {
  background-color: rgba(16, 185, 129, 0.2) !important;
  color: #34d399 !important;
  border-color: rgba(16, 185, 129, 0.35) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .attended-no {
  background-color: rgba(239, 68, 68, 0.2) !important;
  color: #f87171 !important;
  border-color: rgba(239, 68, 68, 0.35) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-pending {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: #94a3b8 !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-contract {
  background-color: #14532d !important;
  color: #86efac !important;
  border: 1px solid rgba(134, 239, 172, 0.2) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-paid {
  background-color: #065f46 !important;
  color: #ffffff !important;
  font-weight: 800;
  border: 1px solid rgba(16, 185, 129, 0.4) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-followup {
  background-color: #7c2d12 !important;
  color: #fed7aa !important;
  border: 1px solid rgba(254, 215, 170, 0.2) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-no-response {
  background-color: #713f12 !important;
  color: #fde047 !important;
  border: 1px solid rgba(253, 224, 71, 0.2) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-not-interested {
  background-color: #7f1d1d !important;
  color: #fca5a5 !important;
  border: 1px solid rgba(252, 165, 165, 0.2) !important;
}

[data-layout-mode="dark"] .recruitment-grid-table .status-left-interview {
  background-color: #1e293b !important;
  color: #cbd5e1 !important;
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}

.ranking-gold-row {
  background-color: rgba(255, 193, 7, 0.08) !important;
  font-weight: 600;
}

[data-layout-mode="dark"] .ranking-gold-row {
  background-color: rgba(255, 193, 7, 0.12) !important;
}

.nav-custom-light .nav-link {
  color: #495057;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.nav-custom-light .nav-link:hover {
  background-color: rgba(64, 81, 137, 0.08);
  color: #405189;
}

.nav-custom-light .nav-link.active {
  background-color: #405189;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(64, 81, 137, 0.35);
}

[data-layout-mode="dark"] .nav-custom-light .nav-link {
  color: #ced4da;
}

[data-layout-mode="dark"] .nav-custom-light .nav-link:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

[data-layout-mode="dark"] .nav-custom-light .nav-link.active {
  background-color: #3b82f6;
  color: #ffffff !important;
}

.dropoff-table td {
  padding: 0.65rem 0.75rem;
}

[data-layout-mode="dark"] .dropoff-table {
  color: #e9ecef;
}

[data-layout-mode="dark"] .dropoff-table tr:hover td {
  background-color: rgba(255, 255, 255, 0.03);
}
</style>
